import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  API_BASE_URL,
  apiRequest,
  clearAuthSession,
  saveAuthSession,
} from "../services/apiClient";

const AuthContext = createContext(null);

const getStoredUser = () => {
  const rawUser = localStorage.getItem("authUser");
  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser);
  } catch (_error) {
    return null;
  }
};

const normalizeRole = (role) => `${role || ""}`.toUpperCase();

const getDefaultRedirectByRole = (role) => {
  switch (normalizeRole(role)) {
    case "SUPERADMIN":
      return "/agent-dashboard";
    case "MARKET_USER":
      return "/market-dashboard";
    default:
      return "/property-listings";
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const [isLoading, setIsLoading] = useState(false);

  const isAuthenticated = Boolean(localStorage.getItem("authToken")) && Boolean(user);

  const login = async ({ email, password }) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const message = Array.isArray(data?.message) ? data.message[0] : data?.message;
        throw new Error(message || "Login failed.");
      }

      saveAuthSession(data);
      setUser(data.user || null);
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async ({ fullName, companyName, email, password }) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName, companyName, email, password }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const message = Array.isArray(data?.message) ? data.message[0] : data?.message;
        throw new Error(message || "Registration failed.");
      }

      saveAuthSession(data);
      setUser(data.user || null);
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiRequest("/auth/logout", { method: "POST", auth: true });
    } catch (_error) {
      // logout should clear local session regardless of API result
    } finally {
      clearAuthSession();
      setUser(null);
    }
  };

  const setSessionFromApi = (sessionData) => {
    saveAuthSession(sessionData);
    setUser(sessionData?.user || null);
  };

  const updateUser = useCallback((nextUser) => {
    if (!nextUser) {
      return;
    }
    saveAuthSession({ user: nextUser });
    setUser(nextUser);
  }, []);

  const hasRole = (...allowedRoles) => {
    const currentRole = normalizeRole(user?.role);
    if (!currentRole) {
      return false;
    }
    return allowedRoles.map(normalizeRole).includes(currentRole);
  };

  const value = useMemo(
    () => ({
      user,
      role: normalizeRole(user?.role),
      isLoading,
      isAuthenticated,
      login,
      register,
      logout,
      hasRole,
      setSessionFromApi,
      updateUser,
      getDefaultRedirectByRole,
    }),
    [user, isLoading, isAuthenticated, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider.");
  }
  return context;
};
